// Event bus centralizado pros eventos do plugin nativo VxlBatchDownloader.
//
// Resolve um leak crônico nos serviços que consumiam `addListener`
// individualmente: no iOS Capacitor 8, plugins registrados via
// `bridge.registerPluginInstance` em `capacitorDidLoad` têm o JSExport
// injetado APÓS o documento carregar — a Promise retornada por
// `addListener` nunca resolve, então o `handle.remove` nunca chega no JS.
// Listeners ficavam órfãos a cada `awaitBatch` (≈4 por download), acumulando
// indefinidamente.
//
// Estratégia: registrar UM listener nativo por evento (4 no total) durante a
// vida do app, manter um Map<batchId, callbacks> em memória, e fazer dispatch
// pelo batchId que vem no payload. `subscribe(batchId, ...)` retorna um
// `unsubscribe()` que apaga a entrada do Map — barato e sem touching nativo.
//
// Trade-off: 4 listeners ficam vivos pra sempre, mas é uma constante (não
// cresce com o uso) e o overhead por evento é só um Map.get sincronicamente.

import { VxlBatchDownloader } from '@/plugins/vxlBatchDownloader';

const subscribers = new Map();
let initialized = false;

function dispatch (kind, data) {
  const batchId = data && data.batchId;
  if (!batchId) return;
  const entry = subscribers.get(batchId);
  if (!entry) return;
  const handler = entry[kind];
  if (typeof handler === 'function') {
    try {
      handler(data);
    } catch (e) {
      console.warn(`[vxlBatchEventBus] handler ${kind} pra ${batchId} lançou:`, e?.message || e);
    }
  }
}

function ensureInitialized () {
  if (initialized) return;
  initialized = true;
  // Fire-and-forget — mesma razão dos serviços antigos: a Promise pode nunca
  // resolver no iOS, mas o handler nativo é instalado sincronamente. Não
  // precisamos do handle porque NUNCA vamos chamar `.remove` (listeners
  // vivem pela vida do app). Catch silencia rejection futura caso ocorra.
  const safe = (event, kind) => {
    const p = VxlBatchDownloader.addListener(event, (data) => dispatch(kind, data));
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };
  safe('batchProgress', 'progress');
  safe('batchCompleted', 'completed');
  safe('batchFailed', 'failed');
  safe('itemFailed', 'itemFailed');
}

/**
 * Inscreve callbacks pra eventos de um batch. Retorna função pra desinscrever.
 *
 * @param {string} batchId
 * @param {{ progress?: Function, completed?: Function, failed?: Function, itemFailed?: Function }} handlers
 * @returns {() => void} unsubscribe
 */
export function subscribeBatch (batchId, handlers) {
  ensureInitialized();
  // Subscribe duplicado pro mesmo batchId sobrescreve — caso patológico (não
  // deveria acontecer com awaitBatch atual), mas pra não silenciar o anterior
  // sem aviso, loga warn.
  if (subscribers.has(batchId)) {
    console.warn(`[vxlBatchEventBus] subscribe duplicado pra batchId=${batchId} — sobrescrevendo`);
  }
  subscribers.set(batchId, { ...handlers });
  return () => { subscribers.delete(batchId); };
}
